import {
  call, put, takeLatest, select, getContext,
} from 'redux-saga/effects';
import { push, replace } from 'react-router-redux';
import qs from 'qs';

import UsersApi from 'apis/supremeGolfApi/UsersApi';
import ReservationsApi from 'apis/supremeGolfApi/ReservationsApi';
import CheckoutActions, { CheckoutTypes, REEDEM_TYPE } from 'reducers/checkout';
import SearchParamsActions from 'reducers/searchParams';
import flowActions from 'reducers/flow';
import GuestActions from 'reducers/guest';

import {
  BOOKING_CONFIRMATION,
  BOOKING_LIMIT_REACHED,
  TEE_TIME_ERROR,
  TEE_TIME_NOT_AVAILABLE,
  TEE_TIME_NOT_ALLOWED,
} from 'utils/routes';
import {
  BOOKING_LIMIT_ERROR_CODE,
  UNALLOWED_TEE_TIMES_ERROR_CODES,
  UNAVAILABLE_TEE_TIMES_ERROR_CODE,
  INTERNAL_CHECKOUT_ERROR_MESSAGES,
  INVALID_TEE_TIMES_ERROR_CODE,
  RATE_LIMIT_ERROR_CODE,
} from 'utils/constants';
import { CVV_MISSING_ERROR_CODE } from 'utils/errorCodes';
import DateHelper from 'utils/dateHelper';
import { parseStringToBool } from 'utils/objectHelper';

const VALIDATION_LIMIT_REACHED = 429;

function* setPromoCodes(preparedTeeTime) {
  const { promoCode, secondaryPromoCode } = preparedTeeTime || {};
  const promoCodes = [];

  if (promoCode) promoCodes.push(promoCode);
  if (secondaryPromoCode) promoCodes.push(secondaryPromoCode);
  if (promoCodes.length) yield put(CheckoutActions.setPromoCodes(promoCodes));
  return promoCodes;
}

function* getTeeTimeSearchInfo() {
  const { filterValues } = yield select((state) => state.search.searchParams);
  const {
    selectedHoles, dateFilter,
    playersFilter, selectedCart, selectedRateType,
  } = filterValues;
  return {
    teeOffAtLocal: dateFilter,
    isRiding: selectedCart,
    numHoles: selectedHoles,
    players: playersFilter,
    rateTypeLabel: selectedRateType.rateTypeLabel,
  };
}

function getDefaultTeeTimeQueryParams({ location, id }, filterValues) {
  const defaultParams = {
    location,
    cart: filterValues.cart,
    date: DateHelper.formatDateWithoutTime(filterValues.dateFilter),
    dealsOnly: filterValues.dealsOnly,
    holes: filterValues.holesFilter,
    locationRange: filterValues.locationRange,
    players: filterValues.playersFilter,
    previousRateTypes: filterValues.previousRateTypes,
    price: filterValues.price,
    rate: filterValues.rate,
    teeTimeId: id,
    time: filterValues.time,
    timeSlot: filterValues.timeSlot,
  };
  return defaultParams;
}

function prepareReservationData(receipt) {
  return {
    transactionId: `TT-${receipt.offer.teeTimeReservationId}`,
    bookingtype: receipt.offer.teeTime.isHotDeal ? 'hotdeal' : 'rack',
    transactionTotal: receipt.offer.sgRevenue.total,
    transactionProducts: [{
      item_id: receipt.offer.teeTime.id.toString(),
      item_name: 'TeeTime',
      price: receipt.offer.sgRevenue.price,
      quantity: receipt.offer.qty,
    }],
  };
}

const unallowedErrorCode = (errorCode) => (
  UNALLOWED_TEE_TIMES_ERROR_CODES.some((code) => code === errorCode)
);

const unavailableErrorCode = (errorCode) => (
  UNAVAILABLE_TEE_TIMES_ERROR_CODE === errorCode || INVALID_TEE_TIMES_ERROR_CODE === errorCode
);

const bookingLimitErrorCode = (errorCode) => (
  BOOKING_LIMIT_ERROR_CODE === errorCode
);

export function* teeTimeFailure(responseCode, extraParams = {}) {
  const searchValues = yield select((state) => state.search.searchParams.searchValues);
  const filterValues = yield select((state) => state.search.searchParams.filterValues);
  const isWebView = yield select(({ app }) => app.isWebView);
  const course = yield select((state) => state.courseDetail.course);
  const { failureSteps } = yield select((state) => state.checkout);
  const defaultQueryParams = getDefaultTeeTimeQueryParams(searchValues, filterValues);
  const { selectedRateType, skipRate } = filterValues;

  const queryString = qs.stringify({
    ...defaultQueryParams,
    selectedRateType,
    ...extraParams,
    skipRate,
    webView: isWebView || null,
    courseId: course?.id || null,
    failureSteps: Object.keys(failureSteps || {}).length ? failureSteps : null,
  }, { skipNulls: true });

  let redirectURL = `${TEE_TIME_ERROR}?${queryString}&errorName=generalError`;
  if (unallowedErrorCode(responseCode)) {
    redirectURL = `${TEE_TIME_NOT_ALLOWED}?${queryString}&errorName=notAllowed`;
  } else if (bookingLimitErrorCode(responseCode)) {
    redirectURL = `${BOOKING_LIMIT_REACHED}?${queryString}&errorName=bookingLimit`;
  } else if (unavailableErrorCode(responseCode) || responseCode === RATE_LIMIT_ERROR_CODE) {
    redirectURL = `${TEE_TIME_NOT_AVAILABLE}?${queryString}&errorName=unAvailable`;
  }

  yield put(GuestActions.resetGuest());
  yield put(CheckoutActions.resetCheckout());
  yield put(flowActions.resetFlow());
  yield put(replace(redirectURL));
}

function* updatePrepareAndPerformCheckoutPreperation(params) {
  const creditCard = yield select((state) => state.checkout.creditCard);
  const features = yield select((state) => state.checkout.features);
  const applyBookingFeeCredit = yield select((state) => state.checkout.applyBookingFeeCredit);
  const donationAmount = yield select((state) => state.checkout.donationAmount);

  const checkoutDisclaimer = yield select((state) => state.checkout.checkoutDisclaimer);

  const providerToken = yield select(({ flow }) => flow?.provider?.authToken);
  const updatedParams = { ...params, applyBookingFeeCredit };
  if (donationAmount) updatedParams.donationAmount = donationAmount;

  const { preparedTeeTime, teeTimeFeatures, authToken } = yield call(
    ReservationsApi.updatePrepareCheckout,
    updatedParams,
    providerToken,
  );
  yield put(flowActions.setfCheckout({ authToken }));
  sessionStorage.setItem('checkoutToken', authToken);

  if (preparedTeeTime.teeTime === null) {
    yield* teeTimeFailure(UNAVAILABLE_TEE_TIMES_ERROR_CODE);
  } else {
    const {
      teeTime, giftCard,
    } = preparedTeeTime || {};
    const { isRiding, numHoles } = teeTime || {};

    if (giftCard?.code && (giftCard?.amount?.amount || giftCard?.cvv)) {
      const giftCardAmount = `${giftCard?.amount?.amount || ''}`;
      yield put(CheckoutActions.setGiftCardAmount(giftCardAmount));
      yield put(CheckoutActions.setGiftCardCvv(giftCard?.cvv || ''));
      updatedParams.giftCard = { code: giftCard.code, amount: giftCardAmount, cvv: giftCard?.cvv || '' };
    }
    const promoCodes = yield* setPromoCodes(preparedTeeTime);
    updatedParams.promoCodes = promoCodes;

    yield put(CheckoutActions.setAppliedModifiers({ ...updatedParams, sync: true }));
    yield put(CheckoutActions.setCartAndHoles(isRiding, numHoles));
    yield put(CheckoutActions.getPrepareDone(
      creditCard,
      preparedTeeTime,
      teeTimeFeatures || features,
      checkoutDisclaimer,
    ));
    if (teeTimeFeatures?.bookingBenefitFailureMessage && !!params.promoCodes?.length) {
      yield put(CheckoutActions.requestValidateRedeemError({
        validatePromoCodeError: teeTimeFeatures.bookingBenefitFailureMessage,
        validateRewardsError: '',
        validateGiftCardError: '',
      }));
    }
    yield put(CheckoutActions.setUpdatePrepareRecent(true));
    yield put(SearchParamsActions.performCheckoutPreparation(
      params.teeTimeId,
      params.prepaymentRuleId,
      undefined,
      preparedTeeTime.teeTime.provider,
      preparedTeeTime.teeTime.sourceProvider,
    ));
  }
}

export function* retryPrepare() {
  const { pathname, search } = yield select((state) => state.router.location);
  const queryParams = qs.parse(`${pathname}${search}`, { ignoreQueryPrefix: true });
  const {
    id,
    players,
    prepaymentRuleId,
    internalOrigin,
  } = parseStringToBool(queryParams);
  yield put(CheckoutActions.getPrepare({
    id,
    players: players ? Number(players) : null,
    prepaymentRuleId: prepaymentRuleId ? Number(prepaymentRuleId) : null,
    internalOrigin,
    reattempt: true,
  }));
}

export function* getPrepareHandler({ params }) {
  try {
    const {
      id: teeTimeId,
      players: qty,
      prepaymentRuleId,
      internalOrigin,
      reattempt = false,
    } = params;

    const LS = yield getContext('localStorage');
    const utmParams = yield call(LS.getUtmParams);
    const rwgParams = yield call(LS.getRwgParams);
    if (internalOrigin) utmParams.utmInternalOrigin = internalOrigin;
    const iterable = yield getContext('iterable');
    const iterableTracking = yield call(iterable.getTrackingParams);
    const creditCardSelected = yield select((state) => state.checkout.creditCard);
    const applyBookingFeeCredit = yield select((state) => state.checkout.applyBookingFeeCredit);

    const providerToken = yield select(({ flow }) => flow?.provider?.authToken);
    const checkoutParams = {
      qty,
      applyBookingFeeCredit,
      teeTimeId,
      prepaymentRuleId,
      utmParams,
      iterableTracking,
      rwgParams,
      reattempt,
    };
    const data = yield call(ReservationsApi.prepareCheckout, checkoutParams, providerToken);
    const { creditCard, preparedTeeTime, features } = data;

    const { loggedIn } = yield select((state) => state.profile);
    if (loggedIn) {
      const paymentSetting = yield call(UsersApi.getUserSetting);
      let paymentMethod = '';
      if (paymentSetting.applePayIsPreferred) paymentMethod = 'applePay';
      else if (paymentSetting.googlePayIsPreferred) paymentMethod = 'googlePay';
      else if (creditCard) paymentMethod = 'card';
      yield put(CheckoutActions.setDefaultPaymentMethod(paymentMethod));
    }

    const { checkoutDisclaimer, authToken } = data;
    yield put(flowActions.setfCheckout({ authToken }));
    sessionStorage.setItem('checkoutToken', authToken);

    const trackGuestPrepare = sessionStorage.getItem('trackGuestPrepare');
    if (trackGuestPrepare) {
      const teeTime = yield* getTeeTimeSearchInfo();
      yield put(GuestActions.trackGuestEvent({
        name: 'guest_continue_success',
        label: 'Guest continue successful',
        category: 'Guest teetime event',
      }, { ...teeTime, teeTimeId }));
      sessionStorage.removeItem('trackGuestPrepare');
    }
    if (preparedTeeTime.teeTime === null) {
      yield* teeTimeFailure(UNAVAILABLE_TEE_TIMES_ERROR_CODE);
      return;
    }
    const appliedModifiers = yield select((state) => state.checkout.appliedModifiers);
    if (reattempt && appliedModifiers?.sync) {
      const { teeTimeReservationId } = preparedTeeTime;
      yield* updatePrepareAndPerformCheckoutPreperation({
        ...appliedModifiers,
        teeTimeId,
        token: teeTimeReservationId,
      });
      return;
    }

    const { isRiding, numHoles } = preparedTeeTime?.teeTime || {};
    yield put(CheckoutActions.setCartAndHoles(isRiding, numHoles));
    yield put(CheckoutActions.getPrepareDone(
      creditCardSelected || creditCard,
      preparedTeeTime,
      features,
      checkoutDisclaimer,
    ));
    yield* setPromoCodes(preparedTeeTime);
  } catch (error) {
    const { response: { data: { error: responseError, failureSteps } = {} } = {} } = error;
    const { response: { status: responseCode } = {} } = error;
    const errorMessage = responseError || error.message;

    if (failureSteps?.retryTeeTime) {
      yield* retryPrepare();
      return;
    }

    yield put(CheckoutActions.setFailureSteps(failureSteps));
    if (responseError === 'Authorization Failed') {
      yield put(SearchParamsActions.performSearch());
      return;
    }

    const trackGuestPrepare = sessionStorage.getItem('trackGuestPrepare');
    if (trackGuestPrepare) {
      const teeTime = yield* getTeeTimeSearchInfo();

      yield put(GuestActions.trackGuestEvent({
        name: 'guest_continue_failed',
        label: 'Guest continue failed',
        category: 'Guest teetime event',
      }, { ...teeTime, teeTimeId: params.id }));
      sessionStorage.removeItem('trackGuestPrepare');
    }

    yield put(CheckoutActions.setFailureSteps(failureSteps));
    if (bookingLimitErrorCode(responseCode)) {
      yield* teeTimeFailure(responseCode, { errorMessage });
    } else if (responseCode) {
      if (unavailableErrorCode(responseCode)) yield* teeTimeFailure(responseCode);
      else yield* teeTimeFailure(responseCode, { errorMessage });
    } else {
      yield put(CheckoutActions.getPrepareError(errorMessage));
    }
  }
}

export function* updatePrepareHandler(action) {
  try {
    const params = {
      qty: action.players,
      teeTimeId: action.teeTimeId,
      token: action.teeTimeReservationId,
      promoCodes: action.promoCodes,
      redeemLoyaltyPoints: action.rewardsPointsAmount,
      giftCard: {
        code: action.giftCardCode,
        amount: action.giftCardAmount,
        cvv: action.giftCardCvv,
      },
      prepaymentRuleId: action.prepaymentRuleId,
    };

    yield updatePrepareAndPerformCheckoutPreperation(params);
  } catch (error) {
    const {
      response: {
        data: { error: explanatoryError, code },
        status: responseCode,
      },
    } = error;
    const errorMessage = explanatoryError || error.message;
    if (code === CVV_MISSING_ERROR_CODE) yield put(CheckoutActions.setIsGiftCardCvvRequired(true));

    if (bookingLimitErrorCode(responseCode)) {
      yield* teeTimeFailure(responseCode, { errorMessage });
    }
    // if (errorMessage === 'Invalid promo code') {
    //   yield put(CheckoutActions.setPromoCode(''));
    // }
    yield put(CheckoutActions.getPrepareError(explanatoryError || error.message));
  }
}

export function* makePaymentHandler({ paymentType, paymentData, cb }) {
  const gtm = yield getContext('gtm');
  try {
    const checkoutToken = yield select(({ flow }) => flow?.checkout?.authToken);
    const { searchParams: { throughSection } } = yield select((state) => state.search);

    const receipt = yield call(
      ReservationsApi.makePayment,
      paymentType,
      { ...paymentData, throughSection },
      checkoutToken,
    );
    if (cb) cb({ success: true });

    const defaultPaymentMethod = yield select((state) => state.checkout.defaultPaymentMethod);

    if (defaultPaymentMethod !== paymentType) {
      yield call(UsersApi.setUserSetting, paymentType);
    }

    yield put(SearchParamsActions.setThroughSection(''));
    yield put(CheckoutActions.makePaymentDone(receipt));

    const {
      receipt: {
        offer: {
          teeTimeReservationId,
        },
      },
    } = receipt;

    yield call(gtm.changeDataLayer, prepareReservationData(receipt.receipt));

    yield call(gtm.trackEvent, {
      eventCategory: 'booking',
      eventAction: 'success',
      eventLabel: teeTimeReservationId,
      event: 'booking-success',
    });

    yield call(gtm.removeTransactionData);
    yield put(CheckoutActions.resetCheckout());
    yield put(flowActions.resetFlow());
    yield put(push(BOOKING_CONFIRMATION.replace(':reservationId', teeTimeReservationId)));
  } catch (error) {
    const {
      response: {
        data: {
          error: explanatoryError,
          isReservationUnconfirmed: isRecoverable,
          failureSteps,
          reservationId,
          reservationUrl,
          isPrepaidOnProvider,
        },
        status: responseCode,
      },
    } = error;
    const errorMessage = explanatoryError || error.message;

    if (cb) cb({ success: false });

    yield call(gtm.trackEvent, {
      eventCategory: 'booking',
      eventAction: 'failure',
      eventLabel: reservationId || 'no reservation id',
      event: 'booking-failure',
    });

    yield put(CheckoutActions.makePaymentError(errorMessage, failureSteps));

    if (failureSteps?.retryTeeTime) {
      yield* retryPrepare(true);
      return;
    }

    if (explanatoryError === 'Authorization Failed') {
      yield put(SearchParamsActions.performSearch());
      return;
    }

    if (!isRecoverable && isPrepaidOnProvider) {
      yield* teeTimeFailure(responseCode, { reservationUrl });
    } else if (!isRecoverable && INTERNAL_CHECKOUT_ERROR_MESSAGES.includes(errorMessage)) {
      yield* teeTimeFailure(responseCode, { errorMessage });
    } else if (!isRecoverable) {
      yield* teeTimeFailure(responseCode);
    }
  }
}

export function* requestValidateRewardsHandler({ redeemType, cb }) {
  try {
    const {
      promoCodes,
      rewardsPointsAmount: redeemLoyaltyPoints,
      preparedTeeTime: {
        teeTimeId,
        teeTimeReservationId: token,
        teeTime: {
          prepaymentRuleId,
        },
      },
      giftCardCode,
      giftCardAmount,
      giftCardCvv,
    } = yield select((state) => state.checkout);
    const { filterValues } = yield select((state) => state.search.searchParams);

    yield updatePrepareAndPerformCheckoutPreperation({
      qty: filterValues.playersFilter,
      teeTimeId,
      token,
      promoCodes,
      redeemLoyaltyPoints,
      giftCard: {
        code: giftCardCode,
        amount: giftCardAmount,
        cvv: giftCardCvv,
      },
      prepaymentRuleId,
    });
    if (cb) cb({ isSuccess: true });
  } catch (error) {
    const {
      response: { data: { error: explanatoryError, code } },
    } = error;

    const errorText = explanatoryError || error.message;
    const errorMessage = {
      validatePromoCodeError: redeemType === REEDEM_TYPE.promoCode ? errorText : '',
      validateRewardsError: redeemType === REEDEM_TYPE.rewards ? errorText : '',
      validateGiftCardError: redeemType === REEDEM_TYPE.giftCard ? errorText : '',
    };
    let reset = false;
    if (error?.response?.status === VALIDATION_LIMIT_REACHED) {
      yield put(CheckoutActions.setIsValidationLimitReached(true));
      errorMessage.validateGiftCardError = redeemType === REEDEM_TYPE.giftCard ? 'Unable to process gift card' : '';
      errorMessage.validatePromoCodeError = redeemType === REEDEM_TYPE.promoCode ? 'Unable to process promo code' : '';
      reset = true;
    }

    if (code === CVV_MISSING_ERROR_CODE) {
      yield put(CheckoutActions.setIsGiftCardCvvRequired(true));
    } else {
      yield put(CheckoutActions.requestValidateRedeemError(errorMessage));
    }
    const isGiftCardCvvRequired = yield select(({ checkout }) => checkout.isGiftCardCvvRequired);
    if (cb) cb({ isSuccess: false, reset, isGiftCardCvvRequired });
  }
}

function* getPrepareWatcher() {
  yield takeLatest(CheckoutTypes.GET_PREPARE, getPrepareHandler);
}

function* updatePrepareWatcher() {
  yield takeLatest(CheckoutTypes.UPDATE_PREPARE, updatePrepareHandler);
}

function* makePaymentWatcher() {
  yield takeLatest(CheckoutTypes.MAKE_PAYMENT, makePaymentHandler);
}

function* requestValidateRewardsWatcher() {
  yield takeLatest(CheckoutTypes.REQUEST_VALIDATE_REDEEM, requestValidateRewardsHandler);
}

export default [
  getPrepareWatcher,
  updatePrepareWatcher,
  makePaymentWatcher,
  requestValidateRewardsWatcher,
];
